<template>
  <b-container v-if="modelAccess === 'denied'">
    <b-alert show variant="danger">
      <translate>You don't have permission to access this section.</translate>
    </b-alert>
  </b-container>
  <div v-else-if="modelAccess === 'granted'">
    <b-container v-if="this.type">
      <h1 v-if="isEdit && !staticItem" v-translate="{ type: $appConfig.models[this.type].title }">Edit %{type}</h1>
      <h1 v-if="!isEdit && !inModal" v-translate="{ type: $appConfig.models[this.type].title }">New %{type}</h1>
      <b-alert
        :show="dismissCountDown"
        dismissible
        variant="success"
        @dismissed="dismissCountDown = 0"
        @dismiss-count-down="countDownChanged"
      >
        <translate>The Item has been updated</translate>
      </b-alert>
      <div class="my-3" v-if="isEdit && $appConfig.models[type].extraActions">
        <hr />
        <span v-for="(action, index) in $appConfig.models[type].extraActions" :key="index" class="mr-1">
          <b-button
            :size="action.size || 'sm'"
            :variant="action.variant || 'primary'"
            @click="action.action(item)"
            :title="action.title || ''"
            style="padding: 0.5rem 0.65rem"
            class="mb-1"
            :disabled="action.disabledCondition && action.disabledCondition(item)"
          >
            <font-awesome-icon
              v-if="action.icon"
              :icon="action.icon"
              size="2x"
              style="font-size: 16px; vertical-align: middle"
            />
            <span v-if="action.label">{{ action.label }}</span>
          </b-button>
        </span>
        <hr />
      </div>
      <b-form @submit.prevent="isEdit ? patchItem() : postItem()">
        <layout-renderer :layout="formLayout">
          <template #field="{ fieldKey }">
        <div :id="'fieldsetHorizontal' + field.key" v-for="field in [fields.find(x => x.key === fieldKey)]" :key="field.key">
          <div
            v-if="
              field &&
              !field.hidden &&
              ((field.key === 'id' && item[field.key]) ||
                (field.key === 'external_id' && item[field.key]) ||
                (field.key !== 'id' && field.key !== 'external_id'))
            "
          >
            <b-form-row class="mb-3" v-if="field.type !== Array || !isEdit">
              <b-col sm="4" lg="3">
                <label :for="'inputHorizontal' + field.key" class="col-form-label"
                  >{{ field.label || snakeCaseToTitleCase(field.key)
                  }}<small class="text-danger" v-if="field.required"> *</small></label
                >
              </b-col>
              <b-col>
                <component
                  :id="'input-' + field.key"
                  v-model="item[field.key]"
                  v-if="field.componentEdit"
                  :is="fields.find(x => x.key === field.key).componentEdit(item[field.key], field.key, item).is"
                  v-b-tooltip.html
                  v-bind="fields.find(x => x.key === field.key).componentEdit(item[field.key], field.key, item).props"
                  v-on="fields.find(x => x.key === field.key).componentEdit(item[field.key], field.key, item).on"
                ></component>
                <b-form-select
                  v-else-if="isEdit && field.type === Boolean"
                  v-model="item[field.key]"
                  :options="[
                    { value: 1, text: $gettext('Yes') },
                    { value: 0, text: $gettext('No') }
                  ]"
                  :disabled="
                    !field.editable ||
                    (field.editCondition && !field.editCondition(item)) ||
                    (field.disabledCondition && field.disabledCondition(item))
                  "
                >
                  <template #first>
                    <option :value="null" v-translate>Select</option>
                  </template>
                </b-form-select>
                <b-form-select
                  v-else-if="isEdit && field.type === 'select' && initialized && !field.showRaw"
                  v-model="item[field.key]"
                  :options="setFieldOptions(field.options, field.withSelect)"
                  :disabled="
                    !field.editable ||
                    (field.editCondition && !field.editCondition(item)) ||
                    (field.disabledCondition && field.disabledCondition(item))
                  "
                >
                  <template #first>
                    <option :value="null" v-translate>Select</option>
                  </template>
                </b-form-select>
                <b-form-select
                  v-else-if="isEdit && field.type === 'select' && initialized && !field.editable && field.showRaw"
                  v-model="item[field.key]"
                  :options="[item[field.key]]"
                  :disabled="!field.editable || (field.disabledCondition && field.disabledCondition(item))"
                ></b-form-select>
                <b-form-select
                  v-else-if="!isEdit && field.type === 'select' && initialized"
                  v-model="item[field.key]"
                  :options="setFieldOptions(field.options, field.withSelect)"
                  :required="field.required"
                  :disabled="
                    (field.editCondition && !field.editCondition(item)) ||
                    (field.disabledCondition && field.disabledCondition(item))
                  "
                >
                  <template #first>
                    <option :value="null" v-translate>Select</option>
                  </template>
                </b-form-select>
                <b-row v-else-if="field.type === 'datetime' && item[field.key + 'DateTimeParts']">
                  <b-col
                    ><b-form-input
                      type="date"
                      v-model="item[field.key + 'DateTimeParts'][0]"
                      :required="isEdit ? null : field.required"
                    ></b-form-input
                  ></b-col>
                  <b-col
                    ><b-form-input
                      type="time"
                      v-model="item[field.key + 'DateTimeParts'][1]"
                      :required="isEdit ? null : field.required"
                    ></b-form-input
                  ></b-col>
                </b-row>
                <b-form-timepicker
                  v-else-if="isEdit && field.type === 'time'"
                  v-model="item[field.key]"
                  :required="field.required"
                  :disabled="
                    !field.editable ||
                    (field.editCondition && !field.editCondition(item)) ||
                    (field.disabledCondition && field.disabledCondition(item))
                  "
                ></b-form-timepicker>
                <b-form-timepicker
                  v-else-if="!isEdit && field.type === 'time'"
                  v-model="item[field.key]"
                  :minutes-step="field.step || 1"
                  :required="field.required"
                  :state="!field.required || (!!item[field.key] && field.required && item[field.key] !== null)"
                  :disabled="
                    (field.editCondition && !field.editCondition(item)) ||
                    (field.disabledCondition && field.disabledCondition(item))
                  "
                ></b-form-timepicker>
                <v-jsoneditor
                  :height="'500px'"
                  v-model="item[field.key]"
                  v-else-if="isEdit && field.type === 'JSON'"
                  :key="'json-' + field.key"
                  :options="{ modes: ['tree', 'view', 'form', 'code', 'text', 'preview'], mode: 'code' }"
                ></v-jsoneditor>
                <span v-else-if="isEdit && field.type === 'link'"
                  ><a :href="item[field.key]" target="_blank">{{ item[field.key] }}</a></span
                >
                <b-form-textarea
                  v-else-if="field.type === 'textarea'"
                  :id="'inputHorizontal' + field.key"
                  v-model="item[field.key]"
                  :required="field.required"
                  :disabled="
                    isEdit
                      ? null
                      : (field.editCondition && !field.editCondition(item)) ||
                        (field.disabledCondition && field.disabledCondition(item))
                  "
                />
                <b-form-input
                  v-else-if="field.type !== Boolean"
                  :id="'inputHorizontal' + field.key"
                  :type="field.type === 'integer' ? 'number' : field.type || 'text'"
                  :step="field.type === 'integer' ? 1 : field.step || null"
                  :pattern="field.pattern || null"
                  :formatter="field.inputFormatter"
                  v-model="item[field.key]"
                  :required="field.required"
                  :disabled="
                    isEdit
                      ? !field.editable ||
                        (field.editCondition && !field.editCondition(item)) ||
                        (field.disabledCondition && field.disabledCondition(item))
                      : (field.editCondition && !field.editCondition(item)) ||
                        (field.disabledCondition && field.disabledCondition(item))
                  "
                />
                <b-form-select
                  v-else
                  v-model="item[field.key]"
                  :options="[
                    { value: 1, text: $gettext('Yes') },
                    { value: 0, text: $gettext('No') }
                  ]"
                  :disabled="
                    (field.editCondition && !field.editCondition(item)) ||
                    (field.disabledCondition && field.disabledCondition(item))
                  "
                />
                <small
                  tabindex="-1"
                  class="form-text text-muted"
                  v-if="field.description"
                  v-html="field.description"
                ></small>
              </b-col>
            </b-form-row>
            <b-form-row v-else>
              <label
                :for="'inputHorizontal' + field.key"
                class="col-form-label"
                v-if="item[field.key]"
                >{{ field.label || snakeCaseToTitleCase(field.key)
                }}<small class="text-danger" v-if="field.required"> *</small></label
              >
              <b-form-input
                v-for="(subitem, i) in item[field.key]"
                v-model="item[field.key][i]"
                :key="i"
                class="mb-3"
              ></b-form-input>
            </b-form-row>
          </div>
        </div>
          </template>
        </layout-renderer>
        <b-form-row v-if="isEdit">
          <b-col cols="12" class="my-3">
            <hr />
          </b-col>
          <b-col>
            <b-button type="submit" variant="primary" v-translate>Submit</b-button>
          </b-col>
          <!-- HACK -->
          <b-col
            v-if="
              type === 'item-packs' &&
              item.status === 'DRAFT' &&
              (!item.serial_numbers || item.serial_numbers.length === 0)
            "
            class="text-center"
          >
            <b-button type="button" variant="warning" v-b-modal.modalPistolScan v-translate>Pistol Scan!</b-button>
            <b-modal
              id="modalPistolScan"
              ref="modalPistolScan"
              title="Start scanning"
              hide-footer
              @shown="$refs.packItemsTexarea.focus()"
              @hide="packItems = ''"
            >
              <form @submit.stop.prevent="submitPackItems">
                <p :class="(packItems.match(/\n/g) || []).length !== item.quantity ? 'text-danger' : 'text-success'">
                  <span v-translate="{ qty: item.quantity }">Expected Items: <strong>%{qty}</strong></span
                  ><br />
                  <span v-translate="{ qty: (packItems.match(/\n/g) || []).length }"
                    >Currently Scanned Items: <strong>%{qty}</strong></span
                  >
                </p>
                <b-form-textarea
                  ref="packItemsTexarea"
                  v-model="packItems"
                  :placeholder="$gettext('Start scanning...')"
                  :state="(packItems.match(/\n/g) || []).length === item.quantity"
                  rows="10"
                  :max-rows="item.quantity"
                ></b-form-textarea>
                <b-form-row>
                  <b-col cols="12" class="my-3">
                    <hr />
                  </b-col>
                  <b-col>
                    <b-button
                      type="submit"
                      variant="primary"
                      :disabled="(packItems.match(/\n/g) || []).length !== item.quantity"
                      v-translate
                      >Submit</b-button
                    >
                  </b-col>
                  <b-col class="text-right" v-if="$appConfig.models[this.type].allowDelete">
                    <b-button @click.stop="$refs.modalPistolScan.hide()" variant="danger" v-translate>Cancel</b-button>
                  </b-col>
                </b-form-row>
              </form>
            </b-modal>
          </b-col>
          <!-- /HACK -->
          <b-col
            class="text-right"
            v-if="$appConfig.models[type].allowDelete && (this.id || this.staticItem?.item?.id)"
          >
            <b-button @click.stop="deleteItem" variant="danger" v-translate>DELETE Item</b-button>
          </b-col>
        </b-form-row>
        <b-button v-else type="submit" variant="primary" v-translate>Submit</b-button>
      </b-form>
      <b-row
        v-if="
          isEdit &&
          $appConfig.models[type].showIncludes &&
          $appConfig.models[type].includes &&
          $appConfig.models[type].includes.length > 0
        "
      >
        // eslint-disable-next-line
        <b-col
          v-for="(include, index) in $appConfig.models[type].includes"
          :key="index"
          v-if="item[include].length > 0"
        >
          <hr />
          <api-list
            :type="include"
            :staticItems="item[include]"
            :title="toTitleCase(include)"
            :fields="$appConfig.models[include].fields"
          />
        </b-col>
      </b-row>
    </b-container>
    <div v-if="isEdit && $appConfig.models[type].editBottomSlot">
      <!-- Edit Bottom Slot is defined at the Model -->
      <component :is="$appConfig.models[type].editBottomSlot" :ref="type + 'EditBottomSlot'" :item="item" />
    </div>
  </div>
</template>
<script>
import VJsoneditor from 'v-jsoneditor/src/index'
import LayoutRenderer from '@/components/Engine/LayoutRenderer'
import { mergeFormLayout } from '@/common/formLayout'
import { getCountries } from '@/services/country'
import { resolveModelAccess } from '@/common/modelAccess'

// Form genérico unificado de edición/alta (MOOV-5200, ex ApiItemEdit +
// ApiItemNew): el modo se deriva de la presencia de `id` (o de un staticItem
// con id, para el edit-in-place de ApiList). Sin id → alta (POST, preData,
// defaults); con id → edición (GET + PATCH, delete, extraActions, includes).
export default {
  name: 'ApiItemForm',
  components: {
    VJsoneditor,
    LayoutRenderer,
    ApiList: () => import('@/components/Engine/ApiList'), // hack for recursive component problem with name
    FleetRentalOrderForm: () => import('@/components/FleetRentalOrderForm')
  },
  props: {
    type: {
      type: String,
      required: true
    },
    id: {
      type: [Number, String],
      required: false
    },
    staticItem: {
      type: Object,
      default: () => {}
    },
    inModal: {
      type: Boolean,
      default: false
    },
    preData: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      initialized: false,
      fields: this.$appConfig.models[this.type].fields.filter(el => el.key !== 'actions'),
      item: {},
      // Item as loaded, handed to the model's beforeEditFilter so it can compare against the edited values
      original: {},
      success: false,
      dismissSecs: 5,
      dismissCountDown: 0,
      // HACK
      packItems: ''
    }
  },
  computed: {
    isEdit() {
      return Boolean(this.id || (this.staticItem && this.staticItem.item))
    },
    modelAccess() {
      return resolveModelAccess(this, this.$appConfig.models[this.type])
    },
    formLayout() {
      const model = this.$appConfig.models[this.type]
      return mergeFormLayout(this.isEdit ? model.editLayout : model.createLayout, this.fields)
    }
  },
  watch: {
    type: function (val) {
      this.fields = this.$appConfig.models[this.type].fields.filter(el => el.key !== 'actions')
    }
  },
  mounted() {
    if (this.modelAccess === 'granted') {
      this.start()
    } else {
      // Models with canAccess wait for the profile; nothing is requested until access is granted
      const unwatch = this.$watch('modelAccess', access => {
        if (access === 'granted') {
          unwatch()
          this.start()
        }
      })
    }
  },
  methods: {
    start() {
      if (this.isEdit) {
        this.$root.$on('reloadItem', () => this.getItem())
        if (!this.staticItem) {
          this.getItem()
        } else {
          this.item = this.staticItem.item
          this.original = JSON.parse(JSON.stringify(this.staticItem.item))
        }
      }
      this.init()
    },
    async init() {
      for (const field of this.fields) {
        if (field.options && !Array.isArray(field.options)) {
          await this['fetch' + field.options.charAt(0).toUpperCase() + field.options.slice(1)]()
        }
        if (!this.isEdit) {
          if (field.default !== undefined) {
            this.item[field.key] = field.default
          }
          if (field.type === 'datetime') {
            if (this.$appConfig.models[this.type].countrize || this.$appConfig.models[this.type].countrizeById) {
              const watchCountryKey =
                'item.' +
                (this.$appConfig.models[this.type].countrize || this.$appConfig.models[this.type].countrizeById)
              this.$watch(
                watchCountryKey,
                val => {
                  if (val) {
                    this.$tz = getCountries().find(x => x.iso_code === val || x.id === val).front_config.i18n.tz
                    this.moment.tz.setDefault(this.$tz)
                  }
                },
                {
                  immediate: true
                }
              )
            }
            this.item[field.key + 'DateTimeParts'] = []
          }
        }
      }
      if (!this.isEdit) {
        this.item = { ...this.item, ...this.preData }
      }
      this.initialized = true
    },
    getItem() {
      const params = {}
      if (this.$appConfig.models[this.type].includes) {
        params.includes = this.$appConfig.models[this.type].includes
      }
      this.$http
        .get(this.$appConfig.api.baseUrl + this.$appConfig.models[this.type].endpoint + '/' + this.id, {
          params
        })
        .then(response => {
          this.item = response.body
          this.original = JSON.parse(JSON.stringify(response.body))
          for (const field of this.fields) {
            if (field.type === 'datetime') {
              if (this.$appConfig.models[this.type].countrize || this.$appConfig.models[this.type].countrizeById) {
                this.$tz = getCountries().find(
                  x =>
                    x.iso_code === this.item[this.$appConfig.models[this.type].countrize] ||
                    x.id === this.item[this.$appConfig.models[this.type].countrizeById]
                ).front_config.i18n.tz
                this.moment.tz.setDefault(this.$tz)
                const watchCountryKey =
                  'item.' +
                  (this.$appConfig.models[this.type].countrize || this.$appConfig.models[this.type].countrizeById)
                this.$watch(
                  watchCountryKey,
                  val => {
                    this.$tz = getCountries().find(x => x.iso_code === val || x.id === val).front_config.i18n.tz
                    this.moment.tz.setDefault(this.$tz)
                  },
                  {
                    immediate: true
                  }
                )
              }
              this.item[field.key + 'DateTimeParts'] = []
              this.item[field.key + 'DateTimeParts'][0] = this.moment
                .utc(this.item[field.key])
                .tz(this.$tz)
                .format('YYYY-MM-DD')
              this.item[field.key + 'DateTimeParts'][1] = this.moment
                .utc(this.item[field.key])
                .tz(this.$tz)
                .format('HH:mm:ss')
            }
          }
        })
    },
    patchItem() {
      let toPatch = {}
      for (const field in this.fields) {
        if (this.fields[field].editable) {
          toPatch[this.fields[field].key] = this.item[this.fields[field].key]
        }
        if (this.fields[field].editable && this.fields[field].toLowerCase) {
          toPatch[this.fields[field].key] = toPatch[this.fields[field].key].toLowerCase()
        }
        if (
          this.fields[field].type === 'time' &&
          !this.fields[field].withSeconds &&
          this.item[this.fields[field].key].split(':').length === 3
        ) {
          const timeParts = this.item[this.fields[field].key].split(':')
          toPatch[this.fields[field].key] = `${timeParts[0]}:${timeParts[1]}`
        }
        if (this.fields[field].type === 'datetime') {
          if (
            this.item[this.fields[field].key + 'DateTimeParts'].length > 0 &&
            this.moment(this.item[this.fields[field].key + 'DateTimeParts'].join(' ')).isValid()
          ) {
            toPatch[this.fields[field].key] = this.moment
              .tz(this.item[this.fields[field].key + 'DateTimeParts'].join(' '), this.$tz)
              .utc()
              .format('YYYY-MM-DD HH:mm:ss')
          } else {
            toPatch[this.fields[field].key] = null
          }
        }
      }

      // Same hook as GCM2's models: the model may reshape the payload right before the request,
      // or return false to cancel it (e.g. the user declined a confirmation)
      if (this.$appConfig.models[this.type].beforeEditFilter) {
        toPatch = this.$appConfig.models[this.type].beforeEditFilter(toPatch, { original: this.original })
        if (toPatch === false) {
          return
        }
      }

      this.$http
        .patch(this.$appConfig.api.baseUrl + this.$appConfig.models[this.type].endpoint + '/' + this.id, toPatch)
        .then(response => {
          this.showAlert()
          if (!this.staticItem) {
            this.getItem()
          } else {
            this.$emit('success', this.staticItem)
          }
        })
    },
    postItem() {
      for (const field in this.fields) {
        if (this.fields[field].toLowerCase) {
          this.item[this.fields[field].key] = this.item[this.fields[field].key].toLowerCase()
        }
        if (
          this.fields[field].type === 'time' &&
          !this.fields[field].withSeconds &&
          this.item[this.fields[field].key].split(':').length === 3
        ) {
          const timeParts = this.item[this.fields[field].key].split(':')
          this.item[this.fields[field].key] = `${timeParts[0]}:${timeParts[1]}`
        }
        if (this.fields[field].type === 'datetime') {
          if (this.item[this.fields[field].key + 'DateTimeParts'].length > 0) {
            this.item[this.fields[field].key] = this.moment
              .tz(this.item[this.fields[field].key + 'DateTimeParts'].join(' '), this.$tz)
              .utc()
              .format('YYYY-MM-DD HH:mm:ss')
          } else {
            this.item[this.fields[field].key] = null
          }
        }
      }
      const toPost = this.$appConfig.models[this.type].beforeCreateFilter
        ? this.$appConfig.models[this.type].beforeCreateFilter({ ...this.item })
        : this.item

      this.$http
        .post(this.$appConfig.api.baseUrl + this.$appConfig.models[this.type].endpoint, toPost)
        .then(response => {
          this.showAlert()
          this.item = response.body
          if (!this.inModal) {
            this.$router.push('/list/' + this.$appConfig.models[this.type].endpoint, { query: { created: true } })
          } else {
            this.$emit('success', this.item)
          }
        })
    },
    deleteItem() {
      if (
        confirm(
          this.$gettextInterpolate(
            this.$gettext(
              'Are you sure you want to DELETE the row with ID:\n%{id}\nWARNING: THIS ACTION CANNOT BE UNDONE!'
            ),
            { id: this.id }
          )
        )
      ) {
        const endpoint = this.$appConfig.models[this.type].endpointDelete
          ? this.$appConfig.models[this.type].endpointDelete.replace('{id}', this.id)
          : this.$appConfig.models[this.type].endpoint + '/' + this.id
        this.$http.delete(this.$appConfig.api.baseUrl + endpoint).then(response => {
          this.$router.push('/list/' + this.$appConfig.models[this.type].endpoint, { query: { deleted: true } })
        })
      }
    },
    countDownChanged(dismissCountDown) {
      this.dismissCountDown = dismissCountDown
    },
    showAlert() {
      this.dismissCountDown = this.dismissSecs
    },
    // HACK
    submitPackItems() {
      const serialNumbers = this.packItems.split('\n').filter(Boolean)
      const dupes = this.checkDuplicates(serialNumbers)
      if (dupes) {
        const mssg = this.$gettextInterpolate(
          this.$gettext(
            '<p>You have scanned DUPLICATED items. Please, REVIEW the serial numbers carefully: </p><ul class="text-left">%{dupes}</ul>'
          ),
          { dupes }
        )
        const obj = {
          title: this.$gettext('ERROR'),
          message: mssg,
          type: 'error'
        }
        this.$root.$children[0].$refs.simplert.openSimplert(obj)
      } else {
        const payload = {
          serial_numbers: serialNumbers
        }
        this.$http.patch(this.$appConfig.api.baseUrl + 'item-packs/' + this.item.id, payload).then(response => {
          this.$refs.modalPistolScan.hide()
          this.getItem()
        })
      }
    },
    checkDuplicates(array) {
      const counts = {}
      let mssg = ''

      for (const item of array) {
        if (counts[item]) {
          counts[item] += 1
        } else {
          counts[item] = 1
        }
      }
      for (const prop in counts) {
        if (counts[prop] >= 2) {
          mssg += this.$gettextInterpolate(this.$gettext('<li>%{prop}: %{qty} times</li>'), {
            prop,
            qty: counts[prop]
          })
        }
      }
      if (mssg) {
        return mssg
      } else {
        return false
      }
    },
    setFieldOptions(fetch, options = false) {
      if (!Array.isArray(fetch)) {
        if (fetch === 'shippingStatusReasons') {
          return Object.entries(this.shippingStatusReasons).reduce(
            (a, [key, value], i) =>
              a.concat(
                value.map(y => {
                  return {
                    text: key + ' - ' + y.name,
                    value: y.reason_id
                  }
                })
              ),
            []
          )
        } else {
          if (!options) {
            return this[fetch]
          } else {
            return this[fetch + 'Select']
          }
        }
      } else {
        return fetch
      }
    }
  }
}
</script>
